import AppLogger from "../utils/appLogger";
import { isIE11 } from "../utils/browser";
import { FeatureFlags, isFeatureEnabled } from "./feature-flags";
import { isQaPublicKey, isSandboxPublicKey } from "../utils";
import { riskConfig } from "../config";
import RiskSdk from "@cko/risk";

class Risk {
  constructor(env, RiskSdk, appUrl, publicKey) {
    this.env = env;
    this.publicKey = publicKey;
    this.appLogger = new AppLogger(appUrl);
    this.timeZone = this._getTimeZone();

    this.riskjs = this._init(RiskSdk);
  }

  _init(RiskSdk) {
    try {
      const isRiskWithIframe = isFeatureEnabled(
        FeatureFlags.UseRiskWithIframe,
        this.env
      );

      const config = this._riskConfig(this.publicKey);

      const riskSettings = {
        framesMode: true,
        publicKey: this.publicKey,
        riskEndpoint: config.riskEndpoint,
        deviceDataEndpoint: config.deviceDataEndpoint,
        riskFrameSrc: isRiskWithIframe ? config.riskFrameSrc : false,
      };

      this.timeout = config.timeout;

      const risk = RiskSdk.init(riskSettings);

      if (risk) {
        risk.addEventListener("riskDataPublishBlocked", (err) => {
          this._logError("riskDataPublishBlocked", err);
        });

        risk.addEventListener("riskDataPublished", (data) => {
          this._logMetrics("riskDataPublished", data.metrics);
        });

        risk.addEventListener("riskDataPublishFailure", (err) => {
          this._logError("riskDataPublishFailure", err);
        });

        risk.addEventListener("riskDataPublishDisabled", (err) => {
          this._logError("riskDataPublishDisabled", err);
        });
      }

      return risk;
    } catch (err) {
      this._logError("riskLibraryInitError", err);

      return null;
    }
  }

  _callWithTimeout(fn, ms) {
    return Promise.race([
      fn,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("timeout")), ms)
      ),
    ]);
  }

  publishData = async (token) => {
    if (!this.riskjs) {
      return;
    }

    try {
      await this._callWithTimeout(
        this.riskjs.publishRiskData(token),
        this.timeout
      );
    } catch {
      // We don't want to log the error here because riskDataPublishFailure will log more details about the error.
    }
  };

  _riskConfig(publicKey) {
    if (isQaPublicKey(publicKey)) {
      return riskConfig.QA;
    }

    if (isSandboxPublicKey(publicKey)) {
      return riskConfig.SBOX;
    }

    return riskConfig.PROD;
  }

  _getTimeZone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return null;
    }
  }

  _getUserAgent() {
    try {
      return window.navigator.userAgent;
    } catch {
      return null;
    }
  }

  _logMetrics(eventName, metrics) {
    this.appLogger.log({
      publicKey: this.publicKey,
      referrer: document.referrer,
      eventName,
      timestamp: new Date().getTime(),
      // Metrics is undefined locally, and JSON.stringify filters out undefined properties.
      // We want the sent data to have a consistent shape, so we set metrics to null if it's undefined.
      metrics: metrics || null,
      timeZone: this.timeZone,
      userAgent: this._getUserAgent(),
    });
  }

  _logError(eventName, error) {
    this.appLogger.log({
      publicKey: this.publicKey,
      referrer: document.referrer,
      eventName,
      timestamp: new Date().getTime(),
      error: error?.message || error,
      errorStack: error?.stack,
      timeZone: this.timeZone,
      userAgent: this._getUserAgent(),
    });
  }
}

export async function createRisk(env, appUrl, publicKey) {
  const isEnabled = isFeatureEnabled(FeatureFlags.Riskjs, env);

  if (env.isRiskDisabled) {
    return null;
  }

  if (isIE11) {
    return null;
  }

  if (!isEnabled) {
    return null;
  }

  return new Risk(env, RiskSdk, appUrl, publicKey);
}
